import Icon from '../ui/Icon';

/**
 * The order summary: line items, what is due today, what recurs, and what is still missing.
 *
 * EXTRACTED FROM ~180 INLINE LINES IN DynamicForm, and the extraction found a real bug class.
 * That markup walked primaryProducts, addonSubscriptions, oneTimeProducts and questions to
 * rebuild a list of line items, re-implementing the conditional-pricing lookup and the
 * radio-versus-checkbox branch that `resolveCart` had already done. It was a sixth copy of the
 * pricing traversal, and copies disagree: the table could show a row the cart did not charge,
 * or omit one it did.
 *
 * It now renders `cart.lineItems` straight through. Those entries arrive with `label`,
 * `sublabel` and `price_display` already resolved by the module that also produces the lookup
 * keys sent to Stripe, so the rows on screen and the amounts charged come from one traversal
 * and cannot drift.
 *
 * THE BLOCKER PANEL LIVES HERE, not with the submit button. The redesign puts the submit button
 * in a fixed bar on phones and in this panel on desktop, so the two are no longer adjacent. The
 * button still points at this panel via aria-describedby using the `blockerListId` the caller
 * owns, which is why that id is a prop rather than a local useId.
 *
 * @param {object} props
 * @param {object} props.cart               resolveCart output
 * @param {string} props.term               'monthly' | 'yearly'
 * @param {object} [props.appliedCoupon]    coupon.applied, for the discount row's description
 * @param {Array}  props.blockers           derived blocker list from useCheckoutForm
 * @param {string} props.blockerListId      id the submit button's aria-describedby points at
 * @param {Function} props.onFocusField     (fieldName) => void
 * @param {React.ReactNode} [props.couponSlot] the CouponField, injected so this stays presentational
 * @param {string} [props.className]
 */
export default function OrderSummary({
  cart,
  term,
  appliedCoupon,
  blockers = [],
  blockerListId,
  onFocusField,
  couponSlot,
  className = '',
}) {
  const { lineItems, totals } = cart;
  const hasBlockers = blockers.length > 0;
  const money = (amount) => `$${amount.toFixed(2)}`;

  /*
    Shown only when it differs from the total. When a yearly plan has no one-time extras, "due
    today $1,118.00 / then $1,118.00 yearly" says the same thing twice and reads like an error.
  */
  const showRecurring = totals.recurringTotal > 0 && totals.recurringTotal !== totals.total;

  const discountDescription = () => {
    if (!appliedCoupon) return 'Discount applied';
    if (appliedCoupon.type !== 'stripe') return appliedCoupon.description;

    const off = appliedCoupon.percent_off
      ? `${appliedCoupon.percent_off}% off`
      : `${money(appliedCoupon.amount_off / 100)} off`;
    return `${appliedCoupon.description}, ${off}`;
  };

  return (
    <section aria-labelledby={`${blockerListId}-heading`} className={className}>
      <h2
        id={`${blockerListId}-heading`}
        className="text-eyebrow font-semibold uppercase text-gray-500"
      >
        Order summary
      </h2>

      {/*
        Definition rows, not a <table>. The old markup was a real table with Items/Price column
        headers, which promises a grid a screen reader user can navigate by row and column. A
        two-column list of label-and-amount pairs is a description list: <dt> is what it is,
        <dd> is what it costs.
      */}
      <dl className="mt-3.5">
        {lineItems.map((item) => (
          <div
            key={item.key}
            className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-2.5"
          >
            <dt className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">{item.label}</span>
              {item.sublabel && (
                <span className="block text-sm text-gray-500">{item.sublabel}</span>
              )}
            </dt>
            <dd className="flex-shrink-0 text-sm font-medium tabular-nums text-gray-900">
              {money(item.price_display)}
            </dd>
          </div>
        ))}

        {totals.discountAmount > 0 && (
          <div className="flex items-baseline justify-between gap-3 border-b border-gray-100 py-2.5">
            <dt className="min-w-0">
              <span className="block text-sm font-medium text-success-600">Discount</span>
              <span className="block text-sm text-gray-500">{discountDescription()}</span>
            </dt>
            {/* Signed, so it is unmistakably a reduction rather than another charge. */}
            <dd className="flex-shrink-0 text-sm font-medium tabular-nums text-success-600">
              -{money(totals.discountAmount)}
            </dd>
          </div>
        )}

        <div className="flex items-baseline justify-between gap-3 pt-3.5">
          <dt className="text-sm font-semibold text-gray-900">Total due today</dt>
          <dd className="text-xl font-semibold tabular-nums text-gray-900">
            {money(totals.total)}
          </dd>
        </div>
      </dl>

      {showRecurring && (
        // Body size, not disclaimer size: what the customer owes every period from now on is one
        // of the most material facts on the page, not fine print.
        <p className="mt-1 text-right text-sm text-gray-500">
          then <span className="tabular-nums">{money(totals.recurringTotal)}</span>{' '}
          {term === 'monthly' ? 'monthly' : 'yearly'}
        </p>
      )}

      {couponSlot && <div className="mt-3">{couponSlot}</div>}

      {/*
        The total changes in response to controls far up the page -- picking a plan, a term, or
        answering a question -- so a screen reader user gets no feedback that the number they
        are being asked to agree to just moved.

        A dedicated sr-only live region rather than aria-live on the amount itself: announcing
        from inside the summary's own markup is unreliable across screen readers, and a bare
        "$2196.00" with no context is close to useless. This announces a full sentence, built
        from the same `cart` object the visible rows render, so the two cannot drift.
      */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {`Order total updated: ${money(totals.total)} ${
          term === 'monthly' ? 'billed monthly' : 'billed yearly'
        }`}
      </div>

      {/*
        Rendered FROM the derived `blockers` list. This replaced three inline recomputations of
        the same predicate plus a hand-written required-field list that could drift from the
        schema.

        Each blocker is a real button that focuses its own field, so the summary is something a
        keyboard user can act on rather than only read.
      */}
      {hasBlockers && (
        <div
          id={blockerListId}
          role="status"
          aria-live="polite"
          className="mt-3 border border-warning-200 bg-warning-50 px-3 py-2.5"
        >
          <div className="flex gap-2 text-sm text-warning-800">
            <Icon name="warningTriangle" className="mt-1 h-4 w-4 flex-shrink-0" />
            {/*
              INLINE, comma-separated, as the mockup draws it: "Still needed: first name, terms
              agreement". That reads only because useCheckoutForm emits noun phrases rather than
              imperative sentences -- see the note there. If those labels ever revert to
              sentences, this becomes an unreadable run-on and should go back to a stacked list.
            */}
            <div>
              <span className="font-medium">Still needed: </span>
              {blockers.map((blocker, index) => (
                <span key={blocker.field || blocker.label}>
                  {index > 0 && ', '}
                  {blocker.field ? (
                    <button
                      type="button"
                      onClick={() => onFocusField(blocker.field)}
                      className="rounded text-left underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-warning-500"
                    >
                      {blocker.label}
                    </button>
                  ) : (
                    // No field to focus (a plan is chosen from cards, not an input), so plain
                    // text rather than a button that would do nothing when pressed.
                    <span className="underline">{blocker.label}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
